import { Fragment, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ArchiveRestore } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ExportButton } from "@/components/Common";
import { MonthPicker, PastMonthBanner } from "@/components/MonthView";
import { FilterBar, SimpleSelect, useRowFilter } from "@/components/Filters";
import { MovementBadge, PositionBadge } from "@/components/Badges";
import { ProductDialog, DeleteProductDialog, RemovedProductsDialog, WeightCell } from "@/components/ProductDialogs";
import { useAuth } from "@/context/AuthContext";
import { useLoad } from "@/hooks/useData";
import { api } from "@/lib/api";
import { fmtINR, fmtNum, monthLabel, monthStr } from "@/lib/format";
import { cn } from "@/lib/utils";
import { usePref } from "@/hooks/usePref";

const sum = (rows, k) => rows.reduce((s, r) => s + (r[k] || 0), 0);

const stockTone = (r, target) => {
  if (r.current_stock <= 0) return "bg-red-100 text-red-700";
  if (r.position === "Over Bought") return "bg-indigo-50 text-indigo-700";
  if (r.reorder_qty > 0) return "bg-amber-500/15 text-amber-200";
  return "bg-emerald-600/15 text-emerald-200";
};

export default function Products() {
  const { isAdmin } = useAuth();
  const [month, setMonth] = useState(monthStr());
  const { data } = useLoad(() => api.get("/report/products", { params: { month } }).then((r) => r.data), [month]);
  const retCounts = useLoad(() => api.get("/returns/product-counts").then((r) => r.data).catch(() => ({})), []).data || {};
  const [movement, setMovement] = usePref("products-movement", "all");
  const [position, setPosition] = usePref("products-position", "all");
  const [weight, setWeight] = usePref("products-weight", "all");
  const extra = useCallback((r) => (movement === "all" || r.movement === movement) && (position === "all" || r.position === position)
    && (weight === "all" || (weight === "missing") === (r.weight_kg == null)), [movement, position, weight]);
  const f = useRowFilter(data?.rows, extra);
  const [edit, setEdit] = useState({ open: false, product: null });
  const [toDelete, setToDelete] = useState(null);
  const [removedOpen, setRemovedOpen] = useState(false);

  if (!data) return <Loading />;
  const lastM = monthLabel(data.history_months[11]);
  const curM = monthLabel(data.month);

  let lastCat = null;
  return (
    <div>
      <PageHeader title="Product Master" subtitle="Stock per warehouse, reorder level, movement & stock position — same logic as your Excel sheet.">
        <MonthPicker month={month} setMonth={setMonth} />
        <ExportButton kind="product_master" params={{ month }} />
        {isAdmin && <Button variant="outline" size="sm" onClick={() => setRemovedOpen(true)} data-testid="removed-products-btn"><ArchiveRestore className="h-4 w-4" /> Removed products</Button>}
        {isAdmin && <Button size="sm" onClick={() => setEdit({ open: true, product: null })} data-testid="add-product-btn"><Plus className="h-4 w-4" /> Add product</Button>}
      </PageHeader>
      <PastMonthBanner data={data} />
      <FilterBar f={f} total={data.rows.length}>
        <SimpleSelect value={movement} onChange={setMovement} testid="filter-movement-select"
          options={[["all", "All movement"], ["Fast", "Fast moving"], ["Medium", "Medium"], ["Slow", "Slow moving"]]} />
        <SimpleSelect value={position} onChange={setPosition} testid="filter-position-select" className="h-9 w-44"
          options={[["all", "All positions"], ["Low Bought", "Low Bought"], ["Balanced", "Balanced"], ["Over Bought", "Over Bought"], ["No Stock / No Sale", "No Stock / Sale"]]} />
        <SimpleSelect value={weight} onChange={setWeight} testid="filter-weight-select"
          options={[["all", "All weights"], ["missing", "Missing weight"], ["has", "Weight entered"]]} />
      </FilterBar>
      <div className="panel overflow-auto max-h-[calc(100vh-260px)]">
        <table className="data-table" data-testid="product-master-table">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 bg-card">Product</th><th className="text-right">Price</th><th className="text-right">Weight (kg)</th>
              <th className="text-right">{lastM} sale</th><th className="text-right">{curM} sale</th>
              <th className="text-right">{data.is_current ? "Current stock" : "Closing stock"}</th><th className="text-right">Reorder (30d)</th>
              <th className="text-right">WH A</th><th className="text-right">WH B</th><th className="text-right">Total (A+B)</th>
              <th>Movement</th><th>Stock position</th><th className="text-right">Value</th><th className="text-right">Returns (90 days)</th><th className="sticky right-0 z-20 bg-card text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {f.filtered.map((r) => {
              const header = r.category !== lastCat;
              lastCat = r.category;
              return (
                <Fragment key={r.id}>
                  {header && (
                    <tr className="bg-red-50/60"><td colSpan={15} className="sticky left-0 text-[11px] font-bold uppercase tracking-[0.2em] text-primary">{r.category}</td></tr>
                  )}
                  <tr data-testid={`product-row-${r.sku}`}>
                    <td className="sticky left-0 z-[5] bg-card max-w-[320px]">
                      <div className="truncate font-medium" title={r.name}>{r.name}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{r.sku}</div>
                    </td>
                    <td className="num text-muted-foreground">{fmtINR(r.price)}</td>
                    <td className="num"><WeightCell product={r} editable={isAdmin && data.is_current} /></td>
                    <td className="num">{r.last_month_sale}</td>
                    <td className={cn("num", r.current_month_sale >= r.current_stock && r.current_month_sale > 0 && "text-amber-300 font-bold")}>{r.current_month_sale}</td>
                    <td className="num"><span className={cn("inline-block min-w-[44px] rounded px-2 py-0.5 font-bold", stockTone(r))} data-testid={`stock-${r.sku}`}>{r.current_stock}</span></td>
                    <td className={cn("num font-bold", r.reorder_qty > 0 ? "text-primary" : "text-muted-foreground")}>{r.reorder_qty}</td>
                    <td className="num text-[#93C5FD]">{r.stock_a}</td><td className="num text-[#DDD6FE]">{r.stock_b}</td>
                    <td className="num font-bold" data-testid={`total-ab-${r.sku}`}>{(r.stock_a || 0) + (r.stock_b || 0)}</td>
                    <td><MovementBadge value={r.movement} /></td>
                    <td><PositionBadge value={r.position} cover={r.cover} /></td>
                    <td className="num text-muted-foreground">{fmtINR(r.stock_value)}</td>
                    <td className="num" data-testid={`returns-90-${r.sku}`}>{retCounts[r.id] ? <Link to={`/returns/register?product_id=${r.id}`} className="font-semibold text-primary hover:underline">{retCounts[r.id]}</Link> : <span className="text-muted-foreground">0</span>}</td>
                    <td className="sticky right-0 z-[5] bg-card whitespace-nowrap border-l border-border/60">
                      {isAdmin && <button className="p-1.5 text-muted-foreground hover:text-primary" title="Edit" onClick={() => setEdit({ open: true, product: r })} data-testid={`edit-btn-${r.sku}`}><Pencil className="h-4 w-4" /></button>}
                      {isAdmin && <button className="p-1.5 text-muted-foreground hover:text-red-400" title="Remove" onClick={() => setToDelete(r)} data-testid={`delete-btn-${r.sku}`}><Trash2 className="h-4 w-4" /></button>}
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="sticky bottom-0 z-10 bg-slate-100 font-bold" data-testid="product-totals-row">
              <td className="sticky left-0 z-20 bg-slate-100">Total ({f.filtered.length} products)</td>
              <td colSpan={4} />
              <td className="num" data-testid="total-current-stock">{fmtNum(sum(f.filtered, "current_stock"))}</td>
              <td />
              <td className="num text-blue-700" data-testid="total-wh-a">{fmtNum(sum(f.filtered, "stock_a"))}</td>
              <td className="num text-violet-700" data-testid="total-wh-b">{fmtNum(sum(f.filtered, "stock_b"))}</td>
              <td className="num" data-testid="total-wh-ab">{fmtNum(sum(f.filtered, "stock_a") + sum(f.filtered, "stock_b"))}</td>
              <td colSpan={4} /><td className="sticky right-0 z-20 bg-slate-100" />
            </tr>
          </tfoot>
        </table>
      </div>
      <ProductDialog open={edit.open} product={edit.product} categories={f.categories} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} />
      <DeleteProductDialog product={toDelete} onClose={() => setToDelete(null)} />
      <RemovedProductsDialog open={removedOpen} onOpenChange={setRemovedOpen} />
    </div>
  );
}
